import { useMemoizedFn } from 'ahooks';

import { ESportsLeftPanelType, EVenue, HotSportId, PlayType } from '@/apis/commonSports/constants';
import { useAppDispatch } from '@/core/store/hooks';
import {
  changeMainListSettings,
  setFollowMatchIds,
  SportState,
  setPinnedSportIds,
  setPinnedMatchIds,
  followMatch,
  setSportsLeftPanelType,
} from '@/core/store/slices/sportSlice';
import { scrollToSportsPageMainAreaIfNeeded } from '@/utils';
import { LocalHandicapItem } from '@/apis/fbSports/common/types';
import { FBCompetitionMap, FBSportIdValue } from '@/apis/fbSports/common/constants';
import { setShowBetDrawer } from '@/core/store/slices/betSlice';

const useSportsMainListControl = () => {
  const dispatch = useAppDispatch();

  // 更改主列表数据显示相关设置
  const setMainListSettings = useMemoizedFn(
    (settings: Partial<SportState['mainList']['settings']> = {}, scrollToTop = true) => {
      void dispatch(changeMainListSettings(settings));
      if (scrollToTop) {
        scrollToSportsPageMainAreaIfNeeded();
      }
    },
  );

  // 获取当前赛种的简洁版赔率玩法项
  const getCurrentSimpleActiveItem = useMemoizedFn((sportId: number) => {
    return (
      Object.values(FBCompetitionMap).find((item) => item.id === sportId)?.simpleList[0] ?? null
    );
  });

  // 切换赛种类型id
  const switchSportId = useMemoizedFn((sportId: number) => {
    setMainListSettings({
      sportId,
      filterByLeagueIds: [],
      filterSearchText: '',
      filterLeaguePickerSynced: true,
      orderBy: 1,
      filterTime: [],
      simpleActiveItem: getCurrentSimpleActiveItem(sportId),
    });
  });

  // 切换赛种类型
  const switchPlayType = useMemoizedFn(
    (playType: PlayType, sportId: number, playTypeId: number) => {
      if (sportId === HotSportId) {
        // 切换赛种类型的时候默认切换到足球赛种
        sportId = FBSportIdValue.Football;
      }
      setMainListSettings({
        playType,
        sportId,
        playTypeId,
        filterByLeagueIds: [],
        filterSearchText: '',
        filterLeaguePickerSynced: true,
        orderBy: 1,
        filterTime: [],
        simpleActiveItem: getCurrentSimpleActiveItem(sportId),
      });
    },
  );

  // 更改关注赛事状态
  const changeFollowMatchStatus = useMemoizedFn(
    (matchInfo: followMatch, type: 'add' | 'remove') => {
      dispatch(setFollowMatchIds({ type, matchInfos: [matchInfo] }));
    },
  );

  // 更改置顶赛种状态
  const changePinnedSportStatus = useMemoizedFn((sportId: number, type: 'add' | 'remove') => {
    dispatch(setPinnedSportIds({ type, sportId }));
  });

  // 更改置顶比赛状态
  const changePinnedMatchStatus = useMemoizedFn((matchId: number, type: 'add' | 'remove') => {
    dispatch(setPinnedMatchIds({ type, matchId }));
  });

  // 更改过滤联赛id（syncLeaguePicker：是否与筛选 Tab 勾选/再次打开默认 Tab 对齐，热门搜索传 false）
  const changeFilterByLeagueIds = useMemoizedFn(
    (
      leagueIds: number[],
      sportId: number,
      searchText: string,
      options?: { syncLeaguePicker?: boolean },
    ) => {
      const cleared = leagueIds.length === 0;
      const syncLeaguePicker = cleared ? true : (options?.syncLeaguePicker ?? true);
      setMainListSettings({
        filterByLeagueIds: leagueIds,
        sportId,
        filterSearchText: searchText,
        filterLeaguePickerSynced: syncLeaguePicker,
      });
    },
  );

  // 更改折叠所有联赛状态
  const changeCollapsedAll = useMemoizedFn((collapsedAll: boolean) => {
    setMainListSettings({ collapsedAll });
  });

  // 更改列表排序方式
  const changeOrderBy = useMemoizedFn((orderBy: number) => {
    setMainListSettings({ orderBy });
  });

  // 早盘更改日期排序方式
  const changeFilterTime = useMemoizedFn((filterTime: number[]) => {
    setMainListSettings({ filterTime });
  });

  // 更改简洁版赔率玩法项
  const changeSimpleActiveItem = useMemoizedFn((simpleActiveItem: LocalHandicapItem) => {
    setMainListSettings({ simpleActiveItem });
  });

  // 更改左侧菜单展示内容
  const switchSportsLeftPanelType = useMemoizedFn((sportsLeftPanelType: ESportsLeftPanelType) => {
    if (sportsLeftPanelType === ESportsLeftPanelType.ORDER_CART) {
      dispatch(setShowBetDrawer({ venue: EVenue.FB, showBetDrawer: true }));
    } else {
      dispatch(setShowBetDrawer({ venue: EVenue.FB, showBetDrawer: false }));
    }
    dispatch(setSportsLeftPanelType(sportsLeftPanelType));
  });

  // 设置当前查询条件下是否含有热门赛事列表
  const setHasHotList = useMemoizedFn((hasHotList: boolean) => {
    setMainListSettings({ hasHotList }, false);
  });

  return {
    switchSportId,
    switchPlayType,
    changeFollowMatchStatus,
    changePinnedSportStatus,
    changePinnedMatchStatus,
    changeFilterByLeagueIds,
    changeCollapsedAll,
    changeOrderBy,
    changeFilterTime,
    changeSimpleActiveItem,
    switchSportsLeftPanelType,
    setHasHotList,
  };
};

export default useSportsMainListControl;
